import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel, Tab, Col, Row, Nav, NavItem } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
import Permission from '../../components/Permission/Permission';

import { useAlertStore } from '../../store/providers/Alert';
import { useMlsProviderStore } from '../../store/providers/MlsProviders';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import { mlsProviderSelect } from '../../store/actions/mlsProvider';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import './MlsProvider.css';

const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const MlsProviderForm = React.lazy(() => import('./MlsProviderForm'));
const NoteTab = React.lazy(() => import('../../components/Tabs/NoteTab/NoteTab'));
const MlsProviderFranchiseTab = React.lazy(() => import('./Tabs/MlsProviderFranchiseTab'));
const MlsProviderFileTab = React.lazy(() => import('./Tabs/MlsProviderFileTab'));
const DirtyModal = React.lazy(() => import('../../components/Modals/DirtyModal/DirtyModal'));

function MlsProvider(props) {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, newRecord, data, clearData } = useMlsProviderStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [title, setTitle] = useState('');
    const [showDirtyModal, setShowDirtyModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState('general');

    const pagePermission = constants.PERMISSION_SETTINGS_MLSPROVIDERS;
    const [hasPermission, setHasPermission] = useState(false);
    const [, setCanEdit] = useState(false);

    const noDirtyClicked = () => {
        setShowDirtyModal(false);
        this.setState({ showDirtyModal: false, selectedTab: selectedTab });
    };

    const yesDirtyClicked = () => {
        setShowDirtyModal(false);
        //this.props.onSetDirty(false);
        //this.setState({ showDirtyModal: false, selectedTab: this.state.nextTab, nextTab: null });
    };

    const cancelledHandler = () => {
        navigate('/mlsProviders');
    };

    const submittedHandler = () => {
        navigate('/mlsProviders');
    };

    const tabSelectedHandler = (tabKey) => {
        if (isDirty && tabKey !== selectedTab) {
            setIsDirty(true);
        } else {
            setSelectedTab(tabKey);
        }
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (!isLoaded && isMounted && data.scope === 'select' && data.status === 'success') {
                setTitle(`Edit MLS Provider (Id: ${data.body.id})`);
                setIsLoaded(true);
                dispatch(breadcrumbAddRoute({
                    key: 'edit-mls-provider',
                    text: data.body.name,
                    path: {
                        event: () => setSelectedTab('general')
                    }
                }));
            }

            if (!isNew && (data.scope === 'new' && data.status === 'success')) {
                setTitle(`New MLS Provider`);
                setIsLoaded(true);
                setIsNew(true);
                dispatch(breadcrumbAddRoute({
                    key: 'new-mls-provider',
                    text: 'New MLS Provider',
                    path: {
                        event: () => setSelectedTab('general')
                    }
                }));
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'update' || data.scope === 'create'));
        }
    }, [props, data, addAlert, clearData, isLoaded, isMounted, isNew, dispatch, setSelectedTab]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'mls-providers', parent: 'Settings', text: 'MLS Providers', path: '/mlsProviders' }));

        if (!_.isEmpty(props.mlsProviderId)) {
            dispatch(mlsProviderSelect({ mlsProviderId: props.mlsProviderId }));       // NOTE: Need this until we convert tabs over to new method.
            selectRecord({ mlsProviderId: props.mlsProviderId });
        } else {
            newRecord();
            dispatch(mlsProviderSelect());   // NOTE: Need this until we convert tabs over to new method.
        }

        setIsMounted(true);
    }, [isMounted, hasPermission, dispatch, newRecord, props.mlsProviderId, selectRecord]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&

                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            showDirtyModal && (
                                <DirtyModal show={showDirtyModal}
                                    noClick={noDirtyClicked}
                                    yesClick={yesDirtyClicked} />
                            )
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title={title} />
                            <Panel>
                                <Panel.Body>
                                    <Tab.Container defaultActiveKey="general" activeKey={selectedTab} onSelect={tabSelectedHandler}>
                                        <Row className="clearfix">
                                            <Col sm={12}>
                                                <Nav bsStyle="pills">
                                                    <NavItem eventKey="general" disabled={isBusy}>General</NavItem>
                                                    <NavItem eventKey="franchises" disabled={isBusy || isNew}>Instances</NavItem>
                                                    <NavItem eventKey="files" disabled={isBusy || isNew}>Files</NavItem>
                                                    <NavItem eventKey="notes" disabled={isBusy || isNew}>Notes</NavItem>
                                                </Nav>
                                            </Col>
                                            <Col sm={12}>
                                                <Tab.Content animation>
                                                    <Tab.Pane eventKey="general">
                                                        <MlsProviderForm
                                                            submitted={() => submittedHandler()}
                                                            cancelled={() => cancelledHandler()} />
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="franchises">
                                                        {
                                                            (!isBusy && !isNew && selectedTab === 'franchises') && <MlsProviderFranchiseTab />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="files">
                                                        {
                                                            (!isBusy && !isNew && selectedTab === 'files') && <MlsProviderFileTab mlsProviderId={data.body.id} title={`Upload File for ${data.body.name}`} postUrl={`/File/MlsProvider/${data.body.id}`} />
                                                        }
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="notes">
                                                        {
                                                            (!isBusy && !isNew && selectedTab === 'notes') && <NoteTab compType="mlsProvider" clearBreadcrumb />
                                                        }
                                                    </Tab.Pane>
                                                </Tab.Content>
                                            </Col>
                                        </Row>
                                    </Tab.Container>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(MlsProvider));